// The tenant frontend information architecture (FRONTEND-BLUEPRINT §1): six persona tabs, each with a
// sub-nav. This is the single source of truth for both the AppShell nav and the router. Existing routes
// are re-homed under personas WITHOUT changing their paths (minimal churn); screens not yet built point at
// placeholder paths (rendered by ModulePlaceholder until their round-2 module lands).
import type { LucideIcon } from "lucide-react";
import {
  ClipboardList, ShieldCheck, CalendarClock, GaugeCircle, Ticket, FileBarChart,
} from "lucide-react";

export interface NavItem {
  /** i18n key OR literal label (resolved in the shell). */ label: string;
  to: string;
  /** permission code gating the link (omit = always visible). */ perm?: string;
  /** true → this path has no module yet (ModulePlaceholder). */ placeholder?: boolean;
}
export interface Persona {
  id: string;
  /** i18n key for the persona tab label. */ label: string;
  icon: LucideIcon;
  /** the default route when the persona tab is clicked. */ home: string;
  items: NavItem[];
}

export const PERSONAS: Persona[] = [
  {
    id: "inspecteur", label: "persona.inspecteur", icon: ClipboardList, home: "/verwerkingen",
    items: [
      { label: "nav.verwerkingen", to: "/verwerkingen", perm: "verwerking.read" },
      { label: "nav.filingSystems", to: "/inspector/filing-systems", perm: "bestandssysteem.read" },
      { label: "nav.assets", to: "/bedrijfsmiddelen", perm: "bedrijfsmiddel.read" },
      { label: "nav.threats", to: "/bedreigingen", perm: "bedreiging.read" },
      { label: "nav.risks", to: "/risico", perm: "risico.read" },
      { label: "nav.regulations", to: "/inspector/regulations", perm: "application_regulation.read" },
      { label: "nav.partners", to: "/partners", perm: "partner_relationship.read" },
      { label: "nav.externalParties", to: "/inspector/external-parties", perm: "partner_relationship.read" },
      // L8 — the five legacy party registers as role-filtered views over the external-party directory.
      { label: "party.controllers", to: "/inspector/controllers", perm: "partner_relationship.read" },
      { label: "party.processors", to: "/inspector/processors", perm: "partner_relationship.read" },
      { label: "party.recipients", to: "/inspector/recipients", perm: "partner_relationship.read" },
      { label: "party.jointControllers", to: "/inspector/joint-controllers", perm: "partner_relationship.read" },
      { label: "party.contacts", to: "/inspector/contacts", perm: "partner_relationship.read" },
      { label: "nav.evidence", to: "/bewijscontainers", perm: "bewijscontainer.read" },
      { label: "nav.transfers", to: "/transfers", perm: "transfer_impact_assessment.read" },
      { label: "nav.dsar", to: "/dsar", perm: "data_subject_request.read" },
    ],
  },
  {
    id: "beleidsmaker", label: "persona.beleidsmaker", icon: ShieldCheck, home: "/policymaker/mission",
    items: [
      // W27-A — live (routes/policymaker/policy-document.tsx); gated on the dedicated aggregate.
      { label: "nav.mission", to: "/policymaker/mission", perm: "policy_document.read" },
      { label: "nav.conduct", to: "/policymaker/conduct", perm: "policy_document.read" },
      { label: "nav.goalPlanning", to: "/policymaker/goals", perm: "policy_document.read" },
    ],
  },
  {
    id: "planner", label: "persona.planner", icon: CalendarClock, home: "/activities/regime",
    items: [
      { label: "nav.regime", to: "/activities/regime", perm: "verwerking.read" },
      { label: "nav.tasks", to: "/activities/tasks", perm: "activity_task.read" },
      { label: "nav.actielijst", to: "/actielijst", perm: "ticket.read" },
      { label: "nav.programs", to: "/programs", perm: "implementation_program.read" },
      { label: "nav.pdca", to: "/pdca", perm: "pdca_schedule.read" },
      { label: "nav.improvements", to: "/verbeterpunten", perm: "implementation_program.read" },
      { label: "nav.sprintGates", to: "/sprint-gates", perm: "sprint_gate_decision.read" },
      { label: "nav.changeFeed", to: "/change-triggers", perm: "change_trigger.read" },
    ],
  },
  {
    id: "controller", label: "persona.controller", icon: GaugeCircle, home: "/",
    items: [
      { label: "nav.overview", to: "/" },
      { label: "nav.accountability", to: "/accountability", perm: "verwerking.read" },
      { label: "nav.dpia", to: "/dpia", perm: "verwerking.read" },
      { label: "nav.risks", to: "/risico", perm: "risico.read" },
    ],
  },
  {
    id: "tickets", label: "persona.tickets", icon: Ticket, home: "/tickets",
    items: [
      { label: "nav.tickets", to: "/tickets", perm: "ticket.read" },
      { label: "nav.dsar", to: "/dsar", perm: "data_subject_request.read" },
    ],
  },
  {
    id: "rapporten", label: "persona.rapporten", icon: FileBarChart, home: "/reports",
    items: [
      { label: "nav.reports", to: "/reports" },
    ],
  },
];

// Org/Gebruiker header-menu surfaces (not persona tabs) — existing routes (FRONTEND-BLUEPRINT §3.7/§3.8).
export const ACCOUNT_MENU: NavItem[] = [
  { label: "nav.orgDetails", to: "/organisatie/gegevens", perm: "tenant_settings.read" }, // Organisatie → Organisatiegegevens (W29)
  { label: "nav.jurisdictietaal", to: "/organisatie/jurisdictietaal", perm: "tenant_settings.read" }, // Organisatie → Jurisdictietaal (P3)
  { label: "nav.vestigingen", to: "/organisatie/vestigingen", perm: "vestiging.read" }, // Organisatie → Vestigingen (W29)
  { label: "nav.privacyteam", to: "/organisatie/privacyteam", perm: "privacyteam_membership.read" }, // Organisatie → Privacyteam (W29)
  { label: "nav.ticketInstellen", to: "/organisatie/ticket-instellen", perm: "tenant_settings.read" }, // Organisatie → Ticket instellen (W30)
  { label: "nav.implPartner", to: "/organisatie/implementatiepartner", perm: "tenant_user.read" }, // Organisatie → Implementatiepartner (W37)
  { label: "nav.archive", to: "/archive", perm: "verwerking.read" }, // Organisatie → Archief (soft-delete recovery; Theme 2)
  { label: "nav.notifications", to: "/notifications", perm: "notification.read" }, // Gebruiker → Meldingen (Theme 4)
  { label: "nav.profile", to: "/me/profile" }, // Gebruiker → Mijn profiel (any signed-in user; W28)
  { label: "me.tickets.title", to: "/me/tickets" },     // L7 Mijn tickets
  { label: "me.cases.title", to: "/me/cases" },         // L7 Mijn cases
  { label: "me.consultant.title", to: "/me/consultant" }, // L7 Mijn consultant
  { label: "me.agr.title", to: "/me/agreements" },      // L6 Mijn overeenkomsten
  { label: "nav.functies", to: "/me/functies" }, // Gebruiker → Mijn functies (read-only; W28)
  { label: "nav.team", to: "/team", perm: "tenant_user.read" },
  { label: "nav.trustExchange", to: "/trust-exchange", perm: "bewijscontainer.read" },
  { label: "nav.consultants", to: "/consultants", perm: "verwerking.read" },
  { label: "nav.auditors", to: "/auditors", perm: "audit_log.read" },
  { label: "nav.billing", to: "/billing", perm: "billing.subscribe" },
  { label: "nav.settings", to: "/settings", perm: "tenant_settings.read" },
];

/** All placeholder paths — the router registers a ModulePlaceholder for each. */
export const PLACEHOLDER_PATHS: string[] = PERSONAS.flatMap((p) => p.items).filter((i) => i.placeholder).map((i) => i.to);
